import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { Button, Card, CardBody, CardHeader, Container, Spinner, Alert, Modal, ModalFooter, ModalBody, ModalHeader } from 'reactstrap';

import useFetch from '../../hooks/useFetch';
import { getCategoryHardDeletePreview, hardDeleteCategory } from '../../redux/methods/categories';

const propTypes =
{
    location: PropTypes.object,
    history: PropTypes.object,
    getCategoryHardDeletePreview: PropTypes.func,
    hardDeleteCategory: PropTypes.func,
    forceRefetch: PropTypes.bool
};

const TreeNode = ({ category, categoriesByParentId, productsByCategoryId, expandedIds, toggleExpanded, level = 0 }) =>
{
    const childCategories = categoriesByParentId.get(category.id) || [];
    const products = productsByCategoryId.get(category.id) || [];
    const isExpanded = expandedIds.has(category.id);
    const hasChildren = childCategories.length > 0 || products.length > 0;

    return (
        <div style={{ marginLeft: `${level * 20}px`, marginBottom: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {hasChildren ? (
                    <Button
                        color="link"
                        className="p-0"
                        onClick={() => toggleExpanded(category.id)}
                    >
                        <i className={`fa ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                    </Button>
                ) : (
                    <span style={{ width: '14px', display: 'inline-block' }} />
                )}

                <strong>{category.name}</strong>
                <span className="text-muted">#{category.id}</span>
            </div>

            {isExpanded && (
                <div>
                    {products.map(product => (
                        <div
                            key={`product-${product.id}-${product.categoryId}`}
                            style={{ marginLeft: '28px', marginTop: '4px' }}
                        >
                            <i className="fa fa-cube mr-2 text-muted" />
                            <span>{product.name}</span>
                            <span className="text-muted ml-2">#{product.id}</span>
                        </div>
                    ))}

                    {childCategories.map(child => (
                        <TreeNode
                            key={child.id}
                            category={child}
                            categoriesByParentId={categoriesByParentId}
                            productsByCategoryId={productsByCategoryId}
                            expandedIds={expandedIds}
                            toggleExpanded={toggleExpanded}
                            level={level + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const CategoriesDelete = ({ location, history, getCategoryHardDeletePreview, hardDeleteCategory, forceRefetch }) =>
{
    const query = new URLSearchParams(location.search);
    const categoryId = query.get('catID');

    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const [preview] = useFetch
    (
        getCategoryHardDeletePreview,
        categoryId,
        [categoryId],
        forceRefetch
    );

    const [expandedIds, setExpandedIds] = useState(new Set());

    useEffect(() =>
    {
        if (preview?.rootCategoryId) { setExpandedIds(new Set([preview.rootCategoryId])); }
    },
    [preview?.rootCategoryId]);

    const toggleExpanded = id =>
    {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) { next.delete(id); }
            else { next.add(id); }
            return next;
        });
    };

    const prepared = useMemo(() =>
    {
        if (!preview) { return null; }

        const categories = preview.categories || [];
        const products = preview.products || [];

        const categoryById = new Map(categories.map(x => [x.id, x]));
        const categoriesByParentId = new Map();
        const productsByCategoryId = new Map();

        for (const category of categories) {
            const key = category.parentId ?? '__root__';
            if (!categoriesByParentId.has(key)) { categoriesByParentId.set(key, []); }
            categoriesByParentId.get(key).push(category);
        }

        for (const product of products) {
            if (!productsByCategoryId.has(product.categoryId)) { productsByCategoryId.set(product.categoryId, []); }
            productsByCategoryId.get(product.categoryId).push(product);
        }

        const root =
            categoryById.get(preview.rootCategoryId)
            || categories.find(x => String(x.id) === String(preview.rootCategoryId))
            || null;

        return {
            root,
            categoriesByParentId,
            productsByCategoryId,
            allCategoryIds: categories.map(x => x.id)
        };
    }, [preview]);

    const expandAll = () =>
    {
        if (!prepared?.allCategoryIds) { return; }
        setExpandedIds(new Set(prepared.allCategoryIds));
    };

    const collapseAll = () =>
    {
        if (!preview?.rootCategoryId)
        {
            setExpandedIds(new Set());
            return;
        }
        setExpandedIds(new Set([preview.rootCategoryId]));
    };

    const handleHardDelete = async () =>
    {
        if (!categoryId) { return; }

        setIsDeleting(true);

        try
        {
            const result = await hardDeleteCategory(categoryId);
            if (result?.ok)
            {
                setIsConfirmOpen(false);
                history.push('/categories');
            }
        }
        finally
        {
            setIsDeleting(false);
        }
    };

    if (!categoryId)
    {
        return <Redirect to="/categories" />;
    }

    if (!preview)
    {
        return (
            <Container fluid>
                <Card>
                    <CardHeader>
                        <i className="fa fa-trash mr-2" />
                        <span>Podgląd twardego usuwania kategorii</span>
                    </CardHeader>
                    <CardBody>
                        <Spinner size="sm" className="mr-2" />
                        <span>Ładowanie...</span>
                    </CardBody>
                </Card>
            </Container>
        );
    }

    return (
        <Container fluid>
            <Card>
                <CardHeader>
                    <i className="fa fa-trash mr-2" />
                    <span>Podgląd twardego usuwania kategorii</span>
                </CardHeader>

                <CardBody>
                    <Alert color="warning">
                        Ta strona pokazuje kategorie i produkty, które zostaną objęte twardym usunięciem.
                    </Alert>

                    <div className="mb-3">
                        <div><strong>ID kategorii:</strong> {preview.rootCategoryId}</div>
                        <div><strong>Liczba kategorii:</strong> {preview.categoriesCount}</div>
                        <div><strong>Liczba produktów:</strong> {preview.productsCount}</div>
                    </div>

                    <div className="mb-3 d-flex" style={{ gap: '8px', flexWrap: 'wrap' }}>
                        <Button color="light" onClick={expandAll}>Expand all</Button>
                        <Button color="light" onClick={collapseAll}>Collapse all</Button>
                        <Button color="danger" onClick={() => setIsConfirmOpen(true)}>Usuń trwale</Button>
                        <Button color="secondary" onClick={() => history.push('/categories')}>Wróć</Button>
                    </div>

                    {prepared?.root ? (
                        <TreeNode
                            category={prepared.root}
                            categoriesByParentId={prepared.categoriesByParentId}
                            productsByCategoryId={prepared.productsByCategoryId}
                            expandedIds={expandedIds}
                            toggleExpanded={toggleExpanded}
                        />
                    ) : (
                        <Alert color="danger">
                            Nie udało się odnaleźć kategorii głównej w odpowiedzi preview.
                        </Alert>
                    )}
                </CardBody>
            </Card>
            {preview.relatedRecords?.length > 0 && (
                <Card className="mb-3">
                    <CardHeader>Powiązane rekordy do usunięcia</CardHeader>
                    <CardBody>
                        <table className="table table-sm">
                            <tbody>
                                {preview.relatedRecords.map(item => (
                                    <tr key={item.name}>
                                        <td>{item.name}</td>
                                        <td className="text-right">{item.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </CardBody>
                </Card>
            )}
            <Modal isOpen={isConfirmOpen} toggle={() => !isDeleting && setIsConfirmOpen(false)}>
                <ModalHeader toggle={() => !isDeleting && setIsConfirmOpen(false)}>
                    Potwierdzenie twardego usunięcia
                </ModalHeader>

                <ModalBody>
                    <p>
                        Ta operacja trwale usunie kategorię, jej podkategorie oraz powiązane produkty.
                    </p>

                    <p className="mb-0">
                        <strong>Liczba kategorii:</strong> {preview.categoriesCount}
                        <br />
                        <strong>Liczba produktów:</strong> {preview.productsCount}
                    </p>
                </ModalBody>

                <ModalFooter>
                    <Button
                        color="secondary"
                        onClick={() => setIsConfirmOpen(false)}
                        disabled={isDeleting}
                    >
                        Anuluj
                    </Button>

                    <Button
                        color="danger"
                        onClick={handleHardDelete}
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'Usuwanie...' : 'Tak, usuń trwale'}
                    </Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
};

CategoriesDelete.propTypes = propTypes;

const mapStateToProps = state => ({
    forceRefetch: state.refetchers.categories
});

const mapDispatch = {
    getCategoryHardDeletePreview,
    hardDeleteCategory
};

export default withRouter(connect(mapStateToProps, mapDispatch)(CategoriesDelete));